import React, { useMemo, useState } from "react";
import {
  CircleHelp,
  ContentContainer,
  Download,
  PageContainer,
  PageHeader,
  PageTopbar,
  astryx,
  neutralTheme,
  useDocumentTheme,
  type BreadcrumbItem,
} from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";

interface DownloadsPageProps {
  currentUser: IUser;
}

type Platform = "mac" | "windows";

// Bump on each desktop release. The S3 bucket sends no CORS headers, so the
// browser can't read RELEASES.json to find the latest version itself.
const RELEASE = {
  version: "2.0.2",
  mac: {
    url: "https://contxtiq-updates.s3.us-east-2.amazonaws.com/contxtiq-electron/darwin/arm64/ContxtIQ-2.0.2-arm64.dmg",
    size: "120 MB",
  },
  windows: {
    url: "https://contxtiq-updates.s3.us-east-2.amazonaws.com/contxtiq-electron/win32/x64/ContxtIQ-2.0.2+Setup.exe",
    size: "146 MB",
  },
};

interface PlatformInfo {
  name: string;
  requirement: string;
  url: string;
  size: string;
  steps: string[];
}

const PLATFORMS: Record<Platform, PlatformInfo> = {
  mac: {
    name: "macOS",
    requirement: "Mac with Apple silicon (M1 or later)",
    url: RELEASE.mac.url,
    size: RELEASE.mac.size,
    steps: [
      "Open the ContxtIQ .dmg file from your Downloads folder.",
      "Drag ContxtIQ into your Applications folder.",
      "Open ContxtIQ and sign in. If macOS asks whether to open an app downloaded from the internet, click Open.",
    ],
  },
  windows: {
    name: "Windows",
    requirement: "Windows 10 or 11 (64-bit)",
    url: RELEASE.windows.url,
    size: RELEASE.windows.size,
    steps: [
      "Open ContxtIQ Setup.exe from your Downloads folder.",
      "ContxtIQ installs and opens on its own. There's no setup wizard.",
      "Sign in with your ContxtIQ account.",
    ],
  },
};

const breadcrumbs: BreadcrumbItem[] = [
  { label: "Desktop app", href: "/dashboard/downloads" },
];

/**
 * Best-effort OS guess. The Mac chip can't be detected reliably (Safari and
 * Firefox report "Intel" on Apple silicon), so the requirement is always shown.
 */
const detectPlatform = (): Platform | null => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return null;
  // iPadOS requests desktop sites with a Mac user agent.
  if (/Mac/i.test(ua)) return navigator.maxTouchPoints > 1 ? null : "mac";
  if (/Win/i.test(ua)) return "windows";
  return null;
};

const PlatformMeta = ({ platform }: { platform: Platform }) => (
  <astryx.Text type="supporting" color="secondary">
    Version {RELEASE.version} · {PLATFORMS[platform].size} · Updates install automatically
  </astryx.Text>
);

// Same quiet info-icon treatment as CardLabel, plus tabIndex so the tooltip
// also opens on keyboard focus.
const IntelHelp = () => (
  <astryx.Tooltip
    focusTrigger="always"
    content={
      "Intel Macs aren't supported yet. To check, open Apple menu → About This Mac: " +
      '"Chip: Apple M1" or later works; "Processor: Intel" won\'t.'
    }
  >
    <astryx.Icon
      icon={CircleHelp}
      size="sm"
      color="secondary"
      tabIndex={0}
      label="Using an Intel Mac?"
    />
  </astryx.Tooltip>
);

interface PlatformCardProps {
  platform: Platform;
  isDetected: boolean;
  onDownload: () => void;
}

const PlatformCard = ({ platform, isDetected, onDownload }: PlatformCardProps) => {
  const info = PLATFORMS[platform];

  return (
    <astryx.Card padding={6} height="100%">
      <astryx.VStack gap={4}>
        <astryx.VStack gap={1}>
          <astryx.HStack gap={2} align="center" wrap="wrap">
            <astryx.Heading level={2}>{info.name}</astryx.Heading>
            {isDetected && <astryx.Badge variant="info" label="This computer" />}
          </astryx.HStack>
          <astryx.HStack gap={2} align="center">
            <astryx.Text color="secondary">{info.requirement}</astryx.Text>
            {platform === "mac" && <IntelHelp />}
          </astryx.HStack>
        </astryx.VStack>
        <astryx.HStack>
          <astryx.Button
            label={`Download for ${info.name}`}
            variant="secondary"
            size="lg"
            icon={<astryx.Icon icon={Download} />}
            href={info.url}
            onClick={onDownload}
          />
        </astryx.HStack>
        <PlatformMeta platform={platform} />
      </astryx.VStack>
    </astryx.Card>
  );
};

const DownloadsPage = (_props: DownloadsPageProps) => {
  const mode = useDocumentTheme();
  const detected = useMemo(detectPlatform, []);
  const [started, setStarted] = useState<Platform | null>(null);

  // Detected OS first so it leads both side by side and when stacked.
  const ordered: Platform[] = detected === "windows" ? ["windows", "mac"] : ["mac", "windows"];

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContentContainer loading={false}>
          <PageHeader
            title="Get the desktop app"
            description="Install ContxtIQ on your computer, then sign in with your ContxtIQ account."
          />

          <astryx.VStack gap={6}>
            {started && (
              <astryx.Banner
                status="success"
                title="Your download has started"
                description={`Finish installing ContxtIQ for ${PLATFORMS[started].name}.`}
                defaultIsExpanded
              >
                <astryx.VStack gap={3}>
                  <astryx.VStack gap={2}>
                    {PLATFORMS[started].steps.map((step, i) => (
                      <astryx.HStack key={step} gap={2} align="start">
                        <astryx.Text color="secondary">{i + 1}.</astryx.Text>
                        <astryx.Text>{step}</astryx.Text>
                      </astryx.HStack>
                    ))}
                  </astryx.VStack>
                  <astryx.HStack gap={2} align="center">
                    <astryx.Text type="supporting" color="secondary">
                      {"Download didn't start?"}
                    </astryx.Text>
                    <astryx.Link type="supporting" href={PLATFORMS[started].url}>
                      Try again
                    </astryx.Link>
                  </astryx.HStack>
                </astryx.VStack>
              </astryx.Banner>
            )}

            {!detected && (
              <astryx.Banner
                status="info"
                title="The ContxtIQ desktop app is for Mac and Windows"
                description="Open this page on your computer to download it."
              />
            )}

            {/* Two columns once the content area fits two 340px cards; stacks below that. */}
            <astryx.Grid columns={{ minWidth: 340, max: 2 }} gap={4} maxWidth={960}>
              {ordered.map((platform) => (
                <PlatformCard
                  key={platform}
                  platform={platform}
                  isDetected={platform === detected}
                  onDownload={() => setStarted(platform)}
                />
              ))}
            </astryx.Grid>
          </astryx.VStack>
        </ContentContainer>
      </PageContainer>
    </astryx.Theme>
  );
};

export default DownloadsPage;
